import * as React from 'react';
import { useMemo } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import useScrollTrigger from '@mui/material/useScrollTrigger';
import Button from '@mui/material/Button';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import { useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import Logo from '../assets/Logo/Company_logo.svg';
import { useLocation } from 'react-router-dom';

export default function Navbar(props) {
    const { t } = useTranslation();
    const theme = useTheme();
    const navItems = useMemo(() => ['home', 'portfolio', 'services', 'about', 'contact'], []);
    const [mobileOpen, setMobileOpen] = React.useState(false);
    const [activeTab, setActiveTab] = React.useState('home');

    // listen to route changes so we can highlight the correct tab
    const location = useLocation();

    const scrollToSection = (sectionId) => {
        const element = document.getElementById(sectionId);
        if (element) {
            const navbarHeight = 80; // Approximate height of the fixed navbar
            const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
            const offsetPosition = elementPosition - navbarHeight;

            window.scrollTo({
                top: offsetPosition,
                behavior: 'smooth'
            });
            setActiveTab(sectionId);
        }
    };

    React.useEffect(() => {
        const observerOptions = {
            root: null,
            rootMargin: '-80px 0px -40% 0px', // Adjusted to account for navbar and center-ish view
            threshold: 0,
        };

        const observerCallback = (entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    setActiveTab(entry.target.id);
                }
            });
        };

        const observer = new IntersectionObserver(observerCallback, observerOptions);

        navItems.forEach((item) => {
            const element = document.getElementById(item);
            if (element) observer.observe(element);
        });

        return () => observer.disconnect();
    }, [navItems]);

    // set portfolio tab active when on a project detail page
    React.useEffect(() => {
        if (location.pathname.startsWith('/portfolio')) {
            setActiveTab('portfolio');
        }
    }, [location]);

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    // Move scroll trigger here to access state for styling
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 0,
        target: typeof window !== 'undefined' ? window : undefined,
    });

    const appBarStyles = {
        backgroundColor: trigger ? theme.palette.custom.navbarBgScrolled : 'transparent',
        boxShadow: trigger ? 4 : 'none',
        transition: 'all 0.3s ease',
        paddingY: trigger ? 0.6 : 1,
        backdropFilter: trigger ? 'blur(10px)' : 'none',
    };

    // when the app bar is transparent (at top of page) the hero
    // background is very light, so white buttons disappear. use the
    // primary text color by default and only switch to the custom
    // scrolled colour once the user has moved.
    const textColor = trigger
        ? theme.palette.custom.navbarTextScrolled || theme.palette.text.primary
        : theme.palette.text.primary;

    // const buttonHoverColor = trigger
    //     ? theme.palette.text.primary
    //     : 'rgba(255, 255, 255, 0.85)';

    const drawerContent = (
        <Box sx={{
            height: '100%',
            background: theme.palette.custom.mobileMenuBg,
            p: 3,
            display: 'flex',
            flexDirection: 'column'
        }}>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    mb: 4,
                    cursor: 'pointer'
                }}
                onClick={() => {
                    scrollToSection('home');
                    handleDrawerToggle();
                }}
            >
                <img src={Logo} alt="NexusLink Logo" style={{ height: '40px' }} />
                <IconButton onClick={handleDrawerToggle} sx={{ color: theme.palette.text.primary }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            <List sx={{ flexGrow: 1 }}>
                {/* {navItems.map((item) => (
                    <ListItem key={item} disablePadding sx={{ mb: 2 }}>
                        <Button
                            fullWidth
                            sx={{
                                justifyContent: 'flex-start',
                                color: theme.palette.text.primary,
                                fontSize: '1.1rem',
                                fontWeight: 400,
                                textTransform: 'none',
                                '&:hover': {
                                    backgroundColor: 'rgba(255, 255, 255, 0.3)',
                                }
                            }}
                        >
                            {t(`layout.navbar.${item}`)}
                        </Button>
                    </ListItem>
                ))} */}
                {navItems.map((item) => {
                    const isActive = activeTab === item;

                    return (
                        <ListItem key={item} disablePadding sx={{ mb: 1 }}>
                            <Button
                                fullWidth
                                onClick={() => {
                                    scrollToSection(item);
                                    handleDrawerToggle();
                                }}
                                sx={{
                                    justifyContent: 'flex-start',
                                    textTransform: 'none',
                                    fontSize: '1.1rem',
                                    fontWeight: isActive ? 600 : 400,
                                    color: isActive
                                        ? theme.palette.primary.main
                                        : theme.palette.text.primary,
                                    px: 2,
                                    py: 1.2,
                                    borderRadius: '8px',
                                    position: 'relative',

                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        left: 0,
                                        top: '50%',
                                        transform: 'translateY(-50%)',
                                        height: '60%',
                                        width: '4px',
                                        borderRadius: '4px',
                                        backgroundColor: theme.palette.primary.main,
                                        opacity: isActive ? 1 : 0,
                                        transition: 'opacity 0.2s ease',
                                    },

                                    '&:hover': {
                                        backgroundColor: 'rgba(255,255,255,0.25)',
                                    },
                                }}
                            >
                                {t(`layout.navbar.${item}`)}
                            </Button>
                        </ListItem>
                    );
                })}
            </List>

            <Button
                variant="contained"
                fullWidth
                onClick={() => {
                    scrollToSection('contact');
                    handleDrawerToggle();
                }}
                sx={{
                    backgroundImage: theme.palette.custom.buttonGradient,
                    color: theme.palette.common.white,
                    fontWeight: 'bold',
                    textTransform: 'none',
                    borderRadius: '8px',
                    py: 1.5,
                    boxShadow: 'none',
                    fontSize: '1rem',
                }}
            >
                {t('layout.navbar.get_quote')}
            </Button>
        </Box>
    );

    return (
        <>
            <AppBar position="fixed" sx={appBarStyles} elevation={trigger ? 4 : 0}>
                <Container maxWidth="xl">
                    <Toolbar disableGutters>
                        {/* Logo */}
                        <Box
                            component="div"
                            onClick={() => scrollToSection('home')}
                            sx={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                cursor: 'pointer'
                            }}
                        >
                            <img src={Logo} alt="NexusLink Logo" style={{ height: '50px', marginRight: '4px' }} />
                        </Box>

                        {/* Desktop Nav Items */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            {/* {navItems.map((item) => (
                                <Button
                                    key={item}
                                    sx={{
                                        color: textColor,
                                        mx: 1,
                                        textTransform: 'none',
                                        fontSize: '.875rem',
                                        fontWeight: 500,
                                        '&:hover': {
                                            color: buttonHoverColor,
                                            backgroundColor: 'transparent',
                                        },
                                    }}
                                >
                                    {t(`layout.navbar.${item}`)}
                                </Button>
                            ))} */}
                            {navItems.map((item) => {
                                const isActive = activeTab === item;

                                return (
                                    <Button
                                        key={item}
                                        onClick={() => scrollToSection(item)}
                                        sx={{
                                            color: isActive ? theme.palette.primary.main : textColor,
                                            mx: 1,
                                            textTransform: 'none',
                                            fontSize: '.875rem',
                                            fontWeight: 500,
                                            position: 'relative',

                                            '&::after': {
                                                content: '""',
                                                position: 'absolute',
                                                bottom: 0,
                                                left: '50%',
                                                transform: 'translateX(-50%)',
                                                width: isActive ? '60%' : '0%',
                                                height: '2px',
                                                backgroundColor: theme.palette.primary.main,
                                                borderRadius: '2px',
                                                transition: 'width 0.3s ease',
                                            },

                                            '&:hover::after': {
                                                width: '60%',
                                            },

                                            '&:hover': {
                                                backgroundColor: 'transparent',
                                            },
                                        }}
                                    >
                                        {t(`layout.navbar.${item}`)}
                                    </Button>
                                );
                            })}

                        </Box>

                        {/* Desktop Get Quote Button */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Button
                                variant="contained"
                                onClick={() => scrollToSection('contact')}
                                sx={{
                                    ml: 2,
                                    backgroundImage: theme.palette.custom.buttonGradient,
                                    color: theme.palette.common.white,
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    borderRadius: '8px',
                                    px: 2,
                                    py: 1,
                                    boxShadow: 'none',
                                    '&:hover': {
                                        backgroundImage: theme.palette.custom.buttonGradient,
                                        opacity: 0.9,
                                        boxShadow: 'none',
                                    },
                                }}
                            >
                                {t('layout.navbar.get_quote')}
                            </Button>
                        </Box>

                        {/* Mobile Menu Button */}
                        <Box sx={{ display: { xs: 'block', md: 'none' } }}>
                            <IconButton
                                color="inherit"
                                aria-label="open drawer"
                                edge="start"
                                onClick={handleDrawerToggle}
                                sx={{ color: textColor }}
                            >
                                <MenuIcon />
                            </IconButton>
                        </Box>
                    </Toolbar>
                </Container>
            </AppBar>

            <Drawer
                anchor="top"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                PaperProps={{
                    sx: {
                        height: 'auto',
                        maxHeight: '90vh',
                        borderRadius: '0 0 20px 20px',
                        overflow: 'hidden'
                    }
                }}
            >
                {drawerContent}
            </Drawer>
        </>
    );
}
